import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { UserProfile, Application } from "services";
import { clearAccessToken, clearRefreshToken } from "utils";

import {
  initUserSessionAction,
  refetchUserPermissionsAction,
  fetchingUserSessionAction,
} from "./thunks";

export interface AuthReducerState {
  status: "AUTH" | "UNAUTH";
  userProfile: UserProfile | null;
  userProfileInitializing: boolean;
  userProfileFetching: boolean;
  userPermissionsRefetching: boolean;
}

const initialState: AuthReducerState = {
  status: "UNAUTH",
  userProfile: null,
  userProfileInitializing: false,
  userProfileFetching: false,
  userPermissionsRefetching: false,
};

export const authSlice = createSlice({
  name: "Auth",
  initialState,
  reducers: {
    logout: (state) => {
      clearAccessToken();
      clearRefreshToken();
      state.status = "UNAUTH";
      state.userProfile = null;
      state.userProfileInitializing = false;
      state.userProfileFetching = false;
      state.userPermissionsRefetching = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchingUserSessionAction.pending, (state) => {
      state.userProfileFetching = true;
    });
    builder.addCase(
      fetchingUserSessionAction.fulfilled,
      (state, action: PayloadAction<UserProfile>) => {
        state.userProfileFetching = false;
        state.status = "AUTH";
        state.userProfile = action.payload;
      }
    );
    builder.addCase(fetchingUserSessionAction.rejected, (state) => {
      state.userProfileFetching = false;
      state.status = "UNAUTH";
      state.userProfile = null;
    });
    builder.addCase(initUserSessionAction.pending, (state) => {
      state.userProfileInitializing = true;
    });
    builder.addCase(
      initUserSessionAction.fulfilled,
      (state, action: PayloadAction<UserProfile>) => {
        state.userProfileInitializing = false;
        state.status = "AUTH";
        state.userProfile = action.payload;
      }
    );
    builder.addCase(initUserSessionAction.rejected, (state) => {
      state.userProfileInitializing = false;
      state.status = "UNAUTH";
      state.userProfile = null;
    });
    builder.addCase(refetchUserPermissionsAction.pending, (state) => {
      state.userPermissionsRefetching = true;
    });
    builder.addCase(
      refetchUserPermissionsAction.fulfilled,
      (
        state,
        action: PayloadAction<{
          applications: Array<Application>;
          permissions: Array<string>;
        }>
      ) => {
        state.userProfileFetching = false;
        state.userProfile = {
          ...(state.userProfile as UserProfile),
          ...action.payload,
        };
      }
    );
    builder.addCase(refetchUserPermissionsAction.rejected, (state) => {
      state.userProfileFetching = false;
      state.userProfile = {
        ...(state.userProfile as UserProfile),
        applications: [],
        permissions: [],
      };
    });
  },
});
